import React, { useState, useEffect } from 'react';
import { TrendingUp, RefreshCw, Crown, Coins, ChevronDown } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { LoadingSpinner } from './LoadingSpinner';

interface RateData {
  metal: 'gold' | 'silver';
  karat: string;
  price: number;
  unit: string;
  lastUpdated: string;
}

const fetchLiveRates = async (): Promise<RateData[]> => {
  try {
    const response = await fetch('/api/rates');
    if (!response.ok) {
        throw new Error('Failed to fetch rates');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching rates:', error);
    return [];
  }
};

export function LiveRates() {
  const [rates, setRates] = useState<RateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  const loadRates = async () => {
    try {
      const newRates = await fetchLiveRates();
      if (newRates && newRates.length > 0) {
          setRates(newRates);
      }
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRates();
    const interval = setInterval(loadRates, 600000);
    return () => clearInterval(interval);
  }, []);

  const handleManualRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    loadRates();
  };

  if (loading && rates.length === 0) {
    return (
      <div className="mb-6 bg-white rounded-lg border border-border p-4">
        <div className="flex items-center justify-center">
          <LoadingSpinner size="sm" className="mr-3" />
          <span className="text-xs text-muted-foreground">Loading rates...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white rounded-xl border border-border overflow-hidden shadow-sm transition-all duration-300">
      {/* Header Bar */}
      <div 
        className="p-3 sm:p-4 flex flex-col cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-3">
                <div className="h-8 w-8 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="h-4 w-4 text-brand-green" />
                </div>
                <h2 className="text-sm font-bold text-brand-green uppercase tracking-tight">Live Market Rates</h2>
            </div>
            
            <div className="flex items-center space-x-2">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleManualRefresh}
                    disabled={loading}
                    className="h-8 w-8 text-brand-green hover:bg-brand-green/10 rounded-full"
                >
                    {loading ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
                <div className={`p-1 rounded-full bg-gray-100 text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                    <ChevronDown className="h-4 w-4" />
                </div>
            </div>
        </div>

        {/* Collapsed Summary Line */}
        {!isExpanded && (
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 ml-0 sm:ml-11">
                {rates.map((rate, i) => (
                    <div key={i} className="flex items-center justify-between sm:justify-start space-x-2 bg-gray-50 px-4 py-2 sm:px-2 sm:py-1 rounded border border-gray-100">
                        <span className="text-xs font-bold text-muted-foreground uppercase">{rate.metal}</span>
                        {rate.karat && (
                            <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded shadow-sm">
                                {rate.karat}
                            </span>
                        )}
                        <span className="text-base font-bold text-brand-green">₹{rate.price.toLocaleString('en-IN')}</span>
                    </div>
                ))}
            </div>
        )}
      </div>

      {/* Expandable Content (CSS Grid Transition) */}
      <div className={`grid transition-all duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden border-t border-dashed border-border/60 bg-gray-50/30">
            <div className="p-4 pt-3">
                <p className="text-xs text-muted-foreground mb-4 text-center">
                    Last updated at: {rates[0]?.lastUpdated}
                </p>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {rates.map((rate, index) => {
                    const isGold = rate.metal === 'gold';
                    const Icon = isGold ? Crown : Coins;
                    const metalColor = isGold ? 'text-brand-gold' : 'text-gray-600';
                    const bgColor = isGold ? 'bg-brand-gold/10' : 'bg-gray-100';
                    
                    return (
                    <Card key={index} className="border-border hover:border-brand-green/30 transition-all shadow-sm bg-white">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center space-x-3">
                                    <div className={`h-10 w-10 rounded-full ${bgColor} flex items-center justify-center`}>
                                        <Icon className={`h-5 w-5 ${metalColor}`} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-xl capitalize">{rate.metal}</h3>
                                        <p className="text-xs text-muted-foreground">per {rate.unit}</p>
                                    </div>
                                </div>
                                
                                {rate.karat && (
                                    <div className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full shadow-sm">
                                        {rate.karat}
                                    </div>
                                )}
                            </div>

                            <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-3xl font-bold text-brand-green">
                                    ₹{rate.price.toLocaleString('en-IN')}
                                    </span>
                                </div>
                                <p className="text-xs text-muted-foreground italic">
                                    Market benchmark price
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                    );
                })}
                </div>

                <div className="mt-8 text-center border-t border-dashed border-border/40 pt-4">
                    <p className="text-xs text-muted-foreground uppercase tracking-widest leading-tight">
                        Indicative prices sourced from Gold API
                    </p>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}